import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { api, formatApiError } from "@/lib/api";
import { ROLES } from "@/lib/constants";
import { useHotel } from "@/context/HotelContext";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/Common";
import { RoleBadge } from "@/components/Badges";
import { ResourceForm } from "@/components/ResourceForm";

const FIELDS = [
  { key: "name", label: "Nome", required: true },
  { key: "email", label: "Email", required: true },
  { key: "role", label: "Ruolo", type: "select", required: true, options: Object.entries(ROLES).map(([k, v]) => ({ value: k, label: v.label })) },
  { key: "password", label: "Password iniziale", required: true },
  { key: "hotel_id", label: "Struttura (obbligatoria tranne Admin e Coordinatore)", type: "hotel" },
];

export default function Utenti() {
  const { hotelName, hotelId } = useHotel();
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const load = useCallback(() => api.get("/users").then((r) => setRows(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const create = async (v) => {
    try { await api.post("/users", v); toast.success("Utente creato"); setOpen(false); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async (u) => {
    if (!window.confirm(`Eliminare ${u.name}?`)) return;
    try { await api.delete(`/users/${u.id}`); toast.success("Utente eliminato"); load(); } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <>
      <PageHeader eyebrow="Accessi e ruoli" title="Utenti" subtitle="Gestisci gli accessi di MSE GROUP e dei referenti delle strutture.">
        <Button className="gap-2" onClick={() => setOpen(true)} data-testid="new-user-btn"><Plus className="w-4 h-4" />Nuovo utente</Button>
      </PageHeader>
      <div className="panel overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="border-slate-200 hover:bg-transparent">
              {["Nome", "Email", "Ruolo", "Struttura", ""].map((h) => <TableHead key={h} className="font-mono text-[11px] uppercase tracking-wider text-slate-500">{h}</TableHead>)}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.filter((u) => hotelId === "all" || !u.hotel_id || u.hotel_id === hotelId).sort((a, b) => (a.hotel_id || "").localeCompare(b.hotel_id || "")).map((u) => (
              <TableRow key={u.id} className="border-slate-200" data-testid="user-row">
                <TableCell className="text-slate-900">{u.name}</TableCell>
                <TableCell className="text-slate-500">{u.email}</TableCell>
                <TableCell><RoleBadge role={u.role} /></TableCell>
                <TableCell className="text-slate-500">{u.hotel_id ? hotelName(u.hotel_id) : "Tutte (accesso globale)"}</TableCell>
                <TableCell className="text-right">
                  {u.id !== user.id && <Button size="icon" variant="ghost" onClick={() => remove(u)} data-testid="user-delete-btn"><Trash2 className="w-4 h-4 text-red-600" /></Button>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <ResourceForm open={open} initial={{ role: "operatore", hotel_id: hotelId !== "all" ? hotelId : undefined }} onClose={() => setOpen(false)} onSubmit={create} fields={FIELDS} title="Nuovo utente" testid="user-form" />
    </>
  );
}
